/**
 * Which programs and claims a rooftop's Claims & Ledger tab reads.
 *
 * Pure, and kept out of the component so they can be unit tested the same way `programVisuals`
 * and `programReports` are.
 */

type Where = Record<string, unknown>;

/**
 * Roles that read a program's claims across every rooftop it covers. The billable customer's
 * users are program admins; Hasura grants the dealer roles only their own rooftop's claims, and
 * does not let them read `billable_customer_id` at all.
 */
export const PROGRAM_WIDE_ROLE = {
  PROGRAM_ADMIN: 'program-admin',
  ADMIN: 'admin',
} as const;
export type ProgramWideRole = (typeof PROGRAM_WIDE_ROLE)[keyof typeof PROGRAM_WIDE_ROLE];

const PROGRAM_WIDE_ROLES: ReadonlySet<string> = new Set(Object.values(PROGRAM_WIDE_ROLE));

export const isProgramWideRole = (role?: string | null): boolean => PROGRAM_WIDE_ROLES.has(role ?? '');

/** The programs a rooftop is enrolled in, and for a program-wide role, the ones that bill it. */
export const rooftopProgramsWhere = (customerId: number, role?: string | null): Where => {
  const enrolled = { programtocustomers: { customer_id: { _eq: customerId } } };
  return isProgramWideRole(role) ? { _or: [enrolled, { billable_customer_id: { _eq: customerId } }] } : enrolled;
};

/**
 * The claims of one program the rooftop's tab lists: the claims on its own moves, and for a
 * program-wide role, every claim of a program that bills the rooftop.
 */
export const rooftopClaimsScope = (programId: number, customerId: number, role?: string | null): Where => {
  const ownMoves = { move: { customer_id: { _eq: customerId } } };
  const visible = isProgramWideRole(role)
    ? { _or: [ownMoves, { program: { billable_customer_id: { _eq: customerId } } }] }
    : ownMoves;
  return { _and: [{ program_id: { _eq: programId } }, visible] };
};

/**
 * Whether the tab reads the whole program, every rooftop it covers, rather than the one rooftop
 * picked in the header. Only a program-wide role does, and only on the customer the program bills:
 * on any other rooftop the scope above narrows to that rooftop's own moves, so a roll-up by rooftop
 * would hold one rooftop and a rooftop filter would offer stores it cannot read.
 */
export const showsWholeProgram = (
  role: string | null | undefined,
  customerId: number,
  // A dealer role's programs have no billable_customer_id, since it cannot read the column.
  program: { id: number; billable_customer_id?: number | null },
): boolean => isProgramWideRole(role) && program.billable_customer_id != null && program.billable_customer_id === customerId;
